import { forwardRef, useState, useEffect, useContext } from "react";
import {
  Box,
  Divider,
  Button,
  IconButton,
  Typography,
  Collapse,
  Tooltip,
} from "@mui/material";
import { Plus, ChevronUp, ChevronDown, Disc } from "lucide-react";
import { format } from "date-fns";
import BlockGutterMap from "./BlockGutterMap";
import { BlockHandle, BlockProps } from "./utils";
import BlockContainer from "./BlockContainer";
import useIsMobile from "components/hooks/useIsMobile";
import {
  useGetTaskBlock,
  useUpdateBlockContent,
  useUpdateBlockMetadata,
} from "services/tasks";
import { minutesToDisplay } from "../utils";
import { TaskTimer } from "protogen/tasks_pb";
import { absoluteTime } from "common/utils";
import { useScrollOnExpand } from "components/hooks/useScrollOnExpand";
import {
  TimeEntry,
  getTotalFromTimeEntries,
  defaultTimeEntry,
  TimeTrackerForm,
  TimeEntryDisplay,
} from "components/common/TimeTracking";
import { TimeRecorderContext } from "components/context/TimeRecorderContext";

interface TimeTrackingMetadata {
  isCollapsed?: boolean;
}

const parseMetadata = (metadata?: string): TimeTrackingMetadata =>
  metadata ? JSON.parse(metadata) : {};

const TotalTimeDisplay = ({ time }: { time: number }) => {
  const totalTimeStr = minutesToDisplay(time);
  return (
    <Box sx={{ display: "flex", gap: "12px" }}>
      <Typography variant="body">Total time</Typography>
      <Typography variant="bodyHeavy">{totalTimeStr}</Typography>
    </Box>
  );
};

export default forwardRef<BlockHandle, BlockProps>(
  ({ block, task, updateTask }: BlockProps, _ref) => {
    const [parsedMetadata, setParsedMetadata] = useState<TimeTrackingMetadata>(
      () => parseMetadata(block.metadata),
    );
    const isMobile = useIsMobile();
    const { session, startRecording } = useContext(TimeRecorderContext);
    const isRecordingThisBlock = session?.taskBlockRef === block.ref;
    const { blockRef: scrollRef, handleExpandWithScroll } = useScrollOnExpand();
    const blockData = JSON.parse(block.content);
    const [timeEntries, setTimeEntries] = useState<TimeEntry[]>(
      blockData.timeEntries || [],
    );
    const [formData, setFormData] = useState<TimeEntry>(defaultTimeEntry());
    const [formVisibility, setFormVisibility] = useState<
      "add" | "edit" | "closed"
    >("closed");
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [totalInMinutes, setTotalInMinutes] = useState(
      getTotalFromTimeEntries(timeEntries),
    );

    const [isSaving, setIsSaving] = useState(false);

    const { request: getBlockRequest } = useGetTaskBlock();

    const refreshBlockData = async () => {
      const response = await getBlockRequest({ taskBlockRef: block.ref });
      if (response?.block?.content) {
        const blockData = JSON.parse(response.block.content);
        setTimeEntries(blockData.timeEntries || []);
        const totalTime = getTotalFromTimeEntries(blockData.timeEntries || []);
        setTotalInMinutes(totalTime);
        const updatedTask = task.clone();
        updatedTask.timing = new TaskTimer({
          minutesReported: totalTime,
          hoursReported: 0,
        });
        updateTask && updateTask(updatedTask);
      }
    };

    useEffect(() => {
      const handleTimeRecordingSaved = (
        event: CustomEvent<{ taskBlockRef: string }>,
      ) => {
        if (event.detail.taskBlockRef === block.ref) {
          refreshBlockData();
        }
      };
      window.addEventListener(
        "timeRecordingSaved",
        handleTimeRecordingSaved as EventListener,
      );
      return () => {
        window.removeEventListener(
          "timeRecordingSaved",
          handleTimeRecordingSaved as EventListener,
        );
      };
    }, [block.ref]);

    const { request } = useUpdateBlockContent((r) => {
      const blockData = JSON.parse(r?.block?.content || "{}");
      setTimeEntries(blockData.timeEntries || []);
      setFormData(defaultTimeEntry());
      setEditingIndex(null);
      setFormVisibility("closed");
      const totalTime = getTotalFromTimeEntries(blockData.timeEntries || []);
      setTotalInMinutes(totalTime);
      const updatedTask = task.clone();
      updatedTask.timing = new TaskTimer({
        minutesReported: totalTime,
        hoursReported: 0, // We are only updating local state
      });
      updateTask && updateTask(updatedTask);
      setIsSaving(false);
    });

    const { request: updateMetadataRequest } = useUpdateBlockMetadata((r) => {
      if (!r.block?.metadata) return;
      setParsedMetadata(JSON.parse(r.block?.metadata));
    });

    const updateTimeEntries = (timeEntries: TimeEntry[]) => {
      if (isSaving) return;
      setIsSaving(true);
      const updatedContent = {
        ...blockData,
        timeEntries: timeEntries,
      };
      request({
        taskBlockRef: block.ref,
        content: JSON.stringify(updatedContent),
        contentType: "json",
        textContent: "", // not used but required in proto
        attachments: [], // not used but required in proto
      });
    };

    const onSave = () => {
      if (editingIndex !== null) {
        // An edit
        const entries = timeEntries.map((entry, i) =>
          i === editingIndex ? formData : entry,
        );
        updateTimeEntries(entries);
      } else {
        // An add
        updateTimeEntries([...timeEntries, formData]);
      }
    };

    const onEditEntry = (index: number) => {
      setEditingIndex(index);
      setFormData(timeEntries[index]);
      setFormVisibility("edit");
    };

    const onDeleteEntry = (index: number) => {
      const newEntries = [...timeEntries];
      newEntries.splice(index, 1);
      updateTimeEntries(newEntries);
    };

    const handleToggleCollapsed = () => {
      const isCurrentlyCollapsed = parsedMetadata.isCollapsed;

      if (isCurrentlyCollapsed) {
        handleExpandWithScroll(() => {
          updateMetadataRequest({
            taskBlockRef: block.ref,
            key: "isCollapsed",
            value: "false",
          });
        });
      } else {
        updateMetadataRequest({
          taskBlockRef: block.ref,
          key: "isCollapsed",
          value: "true",
        });
      }
    };

    const handleAddTime = () => {
      if (parsedMetadata.isCollapsed) {
        handleExpandWithScroll(() => {
          setFormVisibility("add");
          updateMetadataRequest({
            taskBlockRef: block.ref,
            key: "isCollapsed",
            value: "false",
          });
        });
        return;
      }

      setFormVisibility("add");
    };

    useEffect(() => {
      setParsedMetadata(parseMetadata(block.metadata));
    }, [block.metadata]);

    return (
      <BlockContainer
        ref={scrollRef}
        gutterIcon={BlockGutterMap.timeTracking.icon}
        isLast={false}
        advisorViewOnly={true}
        visibilityCTA={
          parsedMetadata.isCollapsed && (
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <Tooltip title="Add time">
                <IconButton
                  aria-label="Add time"
                  size="small"
                  onClick={handleAddTime}
                  sx={{
                    width: "28px",
                    height: "28px",
                    padding: "4px",
                    color: "primary.main",
                  }}
                >
                  <Plus size={16} />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={
                  isRecordingThisBlock
                    ? "Recording time"
                    : session
                      ? "Another timer is recording"
                      : "Record time"
                }
              >
                <Box component="span" sx={{ display: "inline-flex" }}>
                  <IconButton
                    aria-label={
                      isRecordingThisBlock ? "Recording time" : "Record time"
                    }
                    size="small"
                    disabled={!!session}
                    onClick={() => {
                      startRecording(block.ref, task.ref, task.title);
                    }}
                    sx={{
                      width: "28px",
                      height: "28px",
                      padding: "4px",
                      color: "primary.main",
                      "&.Mui-disabled": {
                        color: isRecordingThisBlock
                          ? "primary.main"
                          : "action.disabled",
                      },
                    }}
                  >
                    <Disc size={16} />
                  </IconButton>
                </Box>
              </Tooltip>
            </Box>
          )
        }
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              width: "100%",
              padding: isMobile ? "20px" : "16px 24px",
              gap: parsedMetadata?.isCollapsed ? "0" : "12px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Typography variant="bodyHeavy">Time tracker</Typography>
              </Box>
              <Box onClick={handleToggleCollapsed} sx={{ cursor: "pointer" }}>
                {parsedMetadata?.isCollapsed ? (
                  <ChevronDown size={24} stroke="#8E9598" />
                ) : (
                  <ChevronUp size={24} stroke="#8E9598" />
                )}
              </Box>
            </Box>

            <Collapse in={!parsedMetadata?.isCollapsed} sx={{ width: "100%" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                {timeEntries.length === 0 && formVisibility === "closed" && (
                  <Typography variant="body" color="#6B6E7B">
                    Incrementally record time spent doing activities for this
                    task.
                  </Typography>
                )}
                <Divider sx={{ width: "100%" }} />
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <Typography variant="body">Task created</Typography>
                    <Tooltip
                      placement="top"
                      title={absoluteTime(
                        new Date(Number(task.createdSec) * 1000),
                      )}
                    >
                      <Typography variant="bodySmall" color="#6B6E7B">
                        {format(
                          new Date(Number(task.createdSec) * 1000),
                          "MMM dd",
                        )}
                      </Typography>
                    </Tooltip>
                  </Box>
                  <Box>
                    {timeEntries.length > 0 && formVisibility !== "edit" && (
                      <TimeEntryDisplay
                        entries={timeEntries}
                        onEditEntry={onEditEntry}
                        onDeleteEntry={onDeleteEntry}
                      />
                    )}
                  </Box>
                </Box>
                {(formVisibility === "add" || formVisibility === "edit") && (
                  <TimeTrackerForm
                    title={
                      formVisibility === "add"
                        ? "New time record"
                        : "Edit time record"
                    }
                    updateFormData={(updates: TimeEntry) => {
                      setFormData((prevState) => ({
                        ...prevState,
                        ...updates,
                      }));
                    }}
                    formData={formData}
                    totalInMinutes={totalInMinutes}
                    onSave={onSave}
                    onCancel={() => {
                      setFormVisibility("closed");
                    }}
                    isSaving={isSaving}
                    onDelete={() => {
                      if (editingIndex === null) return;
                      onDeleteEntry(editingIndex);
                    }}
                  />
                )}
                {formVisibility === "closed" && (
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        ...(isMobile && {
                          alignItems: "start",
                          flexDirection: "column",
                          gap: "8px",
                        }),
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        <Button
                          variant="text"
                          onClick={handleAddTime}
                          startIcon={<Plus size={20} />}
                          sx={{ height: "20px" }}
                        >
                          Add time
                        </Button>
                        {" / "}
                        <Button
                          variant="text"
                          disabled={!!session}
                          onClick={() => {
                            startRecording(block.ref, task.ref, task.title);
                          }}
                          startIcon={<Disc size={16} />}
                          sx={{
                            height: "20px",
                            color: session ? "text.secondary" : "primary",
                          }}
                        >
                          {isRecordingThisBlock ? "Recording" : "Record time"}
                        </Button>
                      </Box>
                      {timeEntries.length > 0 && (
                        <TotalTimeDisplay
                          time={totalInMinutes}
                        ></TotalTimeDisplay>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            </Collapse>
          </Box>
        </Box>
      </BlockContainer>
    );
  },
);
